// AppointmentModal.js - Modal for att skapa/redigera bokningar

import React, { useState, useEffect, useRef } from 'react';
import {
  createAppointment, updateAppointment, getPatients,
  createGroupAppointment, checkSchedule, getKallelseTemplates, updatePatient,
  getGroupTemplates, getWaitlist, getGroupMembers, moveWaitlistToMember,
  ensureRealAppointment, isVirtualOccurrence, isSeriesAppointment,
  maxGroupSeriesEndDate, GROUP_SERIES_MAX_MONTHS
} from './calendarApi';
import { useActivePatient } from '../../patient/ActivePatientContext';
import AppointmentCommunicationsSection from '../Communications/AppointmentCommunicationsSection';
import NewPatientForm from '../common/NewPatientForm';
import CopyPnrButton from '../common/CopyPnrButton';
import { formatPnrPasis } from '../../utils/personnummer';
import { getNoShowSummary, noShowCountLabel } from './uteblivetApi';
import { bookedByText } from './bookedBy';
import BookingConfirmationChoice, {
  useConfirmationOptions, defaultChoice, confirmationPayload, patientContacts
} from './BookingConfirmationChoice';
import './Calendar.css';

const AppointmentModal = ({
  appointment,
  initialDate,
  appointmentTypes,
  selectedUserId,
  clinicUsers,
  preSelectedType,
  onClose,
  onSave,
  onEndSeries,
  onCancel,
  onOpenPaymentModal,
  onOpenKallelseModal,
  onOpenGroupParticipants
}) => {
  const isEditing = !!appointment;
  // Spår R: ett virtuellt tillfälle i en återkommande serie bär seriens id.
  // Ändringar gäller som standard bara tillfället (det materialiseras då);
  // "Hela serien" uppdaterar modern utan att ankra om seriens startdatum.
  const isVirtualInstance = isEditing && isVirtualOccurrence(appointment);
  const bookedByLine = isEditing ? bookedByText(appointment) : null; // Spår K2.2
  const [editScope, setEditScope] = useState('occurrence');

  // Spår Q: aktiv patient (öppen journal) förifylls i NYA bokningar.
  const { activePatient } = useActivePatient() || {};

  // Form state
  const [formData, setFormData] = useState({
    patient_id: null,
    appointment_type_id: '',
    start_time: '',
    duration_minutes: 30,
    notes: '',
    status: 'scheduled',
    user_id: selectedUserId || ''
  });

  // Upprepning state
  const [recurrencePattern, setRecurrencePattern] = useState('none');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');

  // Gruppbokning state
  const [isGroupBooking, setIsGroupBooking] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupParticipants, setGroupParticipants] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');
  const [participantResults, setParticipantResults] = useState([]);
  // Deltagarlistan vid öppning (redigering) - ändrad lista i en serie frågar
  // omfång: bara detta tillfälle eller även kommande (Spår G)
  const [initialParticipantIds, setInitialParticipantIds] = useState([]);
  const [participantScope, setParticipantScope] = useState('this');
  // "Hela serien" från ett virtuellt tillfälle: deltagarlistan går inte att ändra
  const rosterLocked = isVirtualInstance && editScope === 'series';
  const participantsChanged = (() => {
    const now = groupParticipants.map(p => p.patient_id).sort();
    const before = [...initialParticipantIds].sort();
    return now.length !== before.length || now.some((id, i) => id !== before[i]);
  })();
  // Inline "ny patient" i gruppläge (G2) - lägg till helt ny patient som deltagare
  const [showNewParticipantForm, setShowNewParticipantForm] = useState(false);

  // Grupp och väntelista state (Spår P2: grupper skapas i Grupper-vyn,
  // här väljs bara en befintlig grupp; medlemmarna följer med automatiskt)
  const [groupTemplates, setGroupTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateWaitlist, setTemplateWaitlist] = useState([]);
  const [loadingWaitlist, setLoadingWaitlist] = useState(false);
  // Väntelistepost som väntar på svar på "permanent eller bara denna gång?"
  // waitlist_id, eller 'all' för Lägg till alla
  const [waitlistChoice, setWaitlistChoice] = useState(null);

  // Schema-override state
  const [scheduleWarning, setScheduleWarning] = useState(null);
  const [scheduleOverride, setScheduleOverride] = useState(false);

  // Kollisionsfråga (I2): {message, availableMinutes} när servern svarat 409
  // för att önskad längd inte ryms före nästa bokning/block
  const [conflictPrompt, setConflictPrompt] = useState(null);

  // Patient search state
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  // Fokus i sökfältet: utan söktext visas senast använda patienter
  const [patientFocused, setPatientFocused] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientForm, setShowPatientForm] = useState(false);
  // Uteblivit-räknare för vald patient (Spår U2)
  const [noShowCount, setNoShowCount] = useState(0);
  const selectedPatientId = selectedPatient?.patient_id;
  useEffect(() => {
    setNoShowCount(0);
    if (!selectedPatientId) return;
    let cancelled = false;
    getNoShowSummary(selectedPatientId)
      .then((data) => { if (!cancelled) setNoShowCount(data.no_show_count || 0); })
      .catch((err) => console.error('Kunde inte hämta uteblivna besök:', err));
    return () => { cancelled = true; };
  }, [selectedPatientId]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Bokningsbekräftelse (Spår B2/B3): förval från bokningstypen, ändringsbart
  // per bokning. Gäller nya bokningar och deltagare som läggs till i en grupp.
  const confirmationOptions = useConfirmationOptions(!isEditing || !!appointment?.is_group);
  const [confirmation, setConfirmation] = useState(null);
  useEffect(() => {
    // Fylls i (om) när bokningstypen väljs/byts
    setConfirmation(defaultChoice(confirmationOptions, formData.appointment_type_id));
  }, [confirmationOptions, formData.appointment_type_id]);
  // Grupp: deltagare som läggs till nu (redigering) får bekräftelse enligt valet
  const addedParticipantCount = groupParticipants
    .filter(p => !initialParticipantIds.includes(p.patient_id)).length;
  const showConfirmationChoice = !isEditing || (isGroupBooking && addedParticipantCount > 0 && !rosterLocked);
  const confirmationFields = () => {
    if (!showConfirmationChoice) return {};
    if (isGroupBooking) return confirmationPayload(confirmation);
    const contacts = patientContacts(selectedPatient);
    return confirmationPayload(confirmation, contacts.sms || contacts.email);
  };

  // Kallelse-state (välj mall att skicka efter bokning)
  const [kallelseTemplates, setKallelseTemplates] = useState([]);
  const [selectedKallelseTemplateId, setSelectedKallelseTemplateId] = useState('');

  // Redigera patientens kontaktuppgifter
  const [editingPatientContact, setEditingPatientContact] = useState(false);
  const [patientContactEdit, setPatientContactEdit] = useState({ email: '', phone: '' });
  const [savingPatientContact, setSavingPatientContact] = useState(false);

  // Initialisera formulär
  useEffect(() => {
    if (appointment) {
      // Beräkna varaktighet från start och slut
      const start = new Date(appointment.start_time);
      const end = new Date(appointment.end_time);
      const durationMs = end - start;
      const durationMins = Math.round(durationMs / 60000);

      setFormData({
        patient_id: appointment.patient_id,
        appointment_type_id: appointment.appointment_type_id || '',
        start_time: formatDateTimeLocal(appointment.start_time),
        duration_minutes: durationMins || 30,
        notes: appointment.notes || '',
        status: appointment.status,
        user_id: appointment.user_id || selectedUserId || ''
      });
      if (appointment.patient) {
        setSelectedPatient(appointment.patient);
      }
      // Hantera upprepning
      if (appointment.recurrence_pattern) {
        setRecurrencePattern(appointment.recurrence_pattern);
      }
      if (appointment.recurrence_end_date) {
        setRecurrenceEndDate(appointment.recurrence_end_date);
      }
      // Hantera gruppbokning
      if (appointment.is_group) {
        setIsGroupBooking(true);
        setGroupName(appointment.group_name || '');
        if (appointment.participants) {
          // Normalisera deltagare - API returnerar patient som nästlat objekt.
          // Avbokade deltagare (egna avbokade bokningar, Spår G) hör inte till listan.
          const normalized = appointment.participants
            .filter(p => p.status !== 'cancelled')
            .map(p => ({
              patient_id: p.patient_id,
              fullname: p.patient?.fullname || p.fullname || `${p.firstname || ''} ${p.lastname || ''}`.trim(),
              personnummer: p.patient?.personnummer || p.personnummer,
              status: p.status
            }));
          setGroupParticipants(normalized);
          setInitialParticipantIds(normalized.map(p => p.patient_id));
        }
      }
    } else if (initialDate) {
      // Förifyll typ från schemaslot, men längden från TYPENS default (I2):
      // tidigare skrev slotlängden tyst över typens längd (60-min-typ i
      // 30-min-slot blev 30 min utan fråga). Ryms längden inte fångas det
      // nu av serverns kollisionskoll som frågar i stället.
      const presetType = preSelectedType?.type_id
        ? (appointmentTypes || []).find(t => t.type_id === preSelectedType.type_id)
        : null;
      setFormData(prev => ({
        ...prev,
        start_time: formatDateTimeLocal(initialDate),
        appointment_type_id: preSelectedType?.type_id || prev.appointment_type_id || '',
        duration_minutes: presetType?.duration_minutes || preSelectedType?.duration_minutes || 30,
        user_id: selectedUserId || ''
      }));
    }

    // Spår Q: förifyll den aktiva patienten (öppen journal) i NYA bokningar —
    // t.ex. dubbelklick i kalendern medan man är "inne på" en patient. Rör inte
    // redigering av befintlig bokning eller gruppbokning.
    if (!appointment && !isGroupBooking && activePatient?.patient_id) {
      setSelectedPatient(activePatient);
      setFormData(prev => ({ ...prev, patient_id: activePatient.patient_id }));
    }
    // appointmentTypes utelämnas medvetet: föräldrarnas loadData ger ny
    // array-identitet efter modalöppning och skulle nollställa användarens
    // pågående ändringar om effekten då körs om.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment, initialDate, selectedUserId, preSelectedType]);

  // Hämta kallelsemallar vid öppning (endast för nya bokningar)
  useEffect(() => {
    const fetchKallelseTemplates = async () => {
      if (isEditing) return; // Hämta inte för redigering
      try {
        const templates = await getKallelseTemplates();
        setKallelseTemplates(templates || []);
      } catch (err) {
        console.error('Error fetching kallelse templates:', err);
      }
    };
    fetchKallelseTemplates();
  }, [isEditing]);

  // Hämta gruppmallar när gruppbokning aktiveras
  useEffect(() => {
    const fetchGroupTemplates = async () => {
      if (!isGroupBooking) return;
      try {
        const templates = await getGroupTemplates();
        setGroupTemplates(templates || []);
      } catch (err) {
        console.error('Error fetching group templates:', err);
      }
    };
    fetchGroupTemplates();
  }, [isGroupBooking]);

  // Hämta väntelista när gruppmall väljs
  useEffect(() => {
    const fetchWaitlist = async () => {
      if (!selectedTemplateId) {
        setTemplateWaitlist([]);
        return;
      }
      try {
        setLoadingWaitlist(true);
        const waitlist = await getWaitlist(selectedTemplateId);
        setTemplateWaitlist(waitlist || []);
      } catch (err) {
        console.error('Error fetching waitlist:', err);
      } finally {
        setLoadingWaitlist(false);
      }
    };
    fetchWaitlist();
  }, [selectedTemplateId]);

  // Formatera datum för datetime-local input (utan UTC-konvertering)
  function formatDateTimeLocal(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  // Sök patienter. Utan söktext (men med fokus i fältet) visas de senast
  // använda patienterna direkt, så man slipper börja skriva för vanliga fall.
  useEffect(() => {
    const searchPatients = async () => {
      if (patientSearch.length < 2) {
        if (!patientFocused) {
          setPatientResults([]);
          return;
        }
        try {
          const response = await getPatients('', {
            sort_by: 'last_appointment', sort_order: 'desc', limit: 5
          });
          const patients = response.patients || response;
          setPatientResults(Array.isArray(patients) ? patients : []);
        } catch (err) {
          console.error('Error fetching recent patients:', err);
        }
        return;
      }
      try {
        const response = await getPatients(patientSearch);
        // API returnerar { patients: [...], total, ... } eller direkt array
        const patients = response.patients || response;
        setPatientResults(Array.isArray(patients) ? patients : []);
      } catch (err) {
        console.error('Error searching patients:', err);
      }
    };

    const debounce = setTimeout(searchPatients, 300);
    return () => clearTimeout(debounce);
  }, [patientSearch, patientFocused]);

  // Sök deltagare för gruppbokning
  useEffect(() => {
    const searchParticipants = async () => {
      if (participantSearch.length < 2) {
        setParticipantResults([]);
        return;
      }
      try {
        const response = await getPatients(participantSearch);
        const patients = response.patients || response;
        // Filtrera bort redan tillagda deltagare
        const existingIds = groupParticipants.map(p => p.patient_id);
        const filtered = (Array.isArray(patients) ? patients : [])
          .filter(p => !existingIds.includes(p.patient_id));
        setParticipantResults(filtered);
      } catch (err) {
        console.error('Error searching participants:', err);
      }
    };

    const debounce = setTimeout(searchParticipants, 300);
    return () => clearTimeout(debounce);
  }, [participantSearch, groupParticipants]);

  // Kontrollera schema när tid/behandlare ändras
  useEffect(() => {
    const validateSchedule = async () => {
      if (!formData.start_time || !formData.user_id) {
        setScheduleWarning(null);
        return;
      }

      try {
        // Sluttiden måste skickas i samma naiva lokalformat som start_time.
        // toISOString() ger UTC ('Z'), vilket backend jämför rått mot lokala
        // arbetstider → sluttiden förskjuts 1-2 h och varningen "utanför
        // arbetstid" triggas felaktigt för nästan varje bokning.
        const endTime = new Date(new Date(formData.start_time).getTime() + (formData.duration_minutes || 30) * 60000);
        const result = await checkSchedule(
          parseInt(formData.user_id),
          formData.start_time,
          formatDateTimeLocal(endTime)
        );

        if (!result.within_schedule) {
          setScheduleWarning(result.message || 'Tiden är utanför behandlarens arbetstid');
        } else {
          setScheduleWarning(null);
          setScheduleOverride(false);
        }
      } catch (err) {
        // Ignorera om schemat inte finns
        setScheduleWarning(null);
      }
    };

    const debounce = setTimeout(validateSchedule, 500);
    return () => clearTimeout(debounce);
  }, [formData.start_time, formData.user_id, formData.duration_minutes]);

  // Välj patient
  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setFormData(prev => ({ ...prev, patient_id: patient.patient_id }));
    setPatientSearch('');
    setPatientResults([]);
  };

  // Rensa vald patient
  const handleClearPatient = () => {
    setSelectedPatient(null);
    setFormData(prev => ({ ...prev, patient_id: null }));
  };

  // Lägg till deltagare i gruppbokning
  const handleAddParticipant = (patient) => {
    setGroupParticipants(prev => [...prev, { ...patient, status: 'confirmed' }]);
    setParticipantSearch('');
    setParticipantResults([]);
  };

  // Ta bort deltagare från gruppbokning
  const handleRemoveParticipant = (patientId) => {
    setGroupParticipants(prev => prev.filter(p => p.patient_id !== patientId));
  };

  // Väntelisteplock (Spår P2): frågan "permanent eller bara denna gång?"
  // ställs direkt vid tillägget, men båda valen är bara klientstate tills
  // bokningen sparas - permanent-flytten verkställs i doSubmit EFTER att
  // bokningen skapats, så Avbryt lämnar inga halvgjorda gruppändringar.
  const applyWaitlistAdd = (entries, permanent) => {
    const existingIds = groupParticipants.map(p => p.patient_id);
    const toAdd = entries.filter(
      entry => entry.patient && !existingIds.includes(entry.patient.patient_id)
    );
    setGroupParticipants(prev => [
      ...prev,
      ...toAdd.map(entry => ({
        patient_id: entry.patient.patient_id,
        fullname: entry.patient.fullname,
        personnummer: entry.patient.personnummer,
        status: 'confirmed',
        from_waitlist: true,
        waitlist_id: entry.waitlist_id,
        // Kom ihåg vilken grupp patienten plockades från, så flytten går
        // mot rätt väntelista även om gruppvalet byts efteråt
        waitlist_template_id: selectedTemplateId,
        pending_member: permanent
      }))
    ]);
    setWaitlistChoice(null);
  };

  // Toggle mellan enkel- och gruppbokning
  const handleToggleGroupBooking = () => {
    setIsGroupBooking(!isGroupBooking);
    if (!isGroupBooking) {
      // Byter till gruppbokning - rensa enskild patient
      setSelectedPatient(null);
      setFormData(prev => ({ ...prev, patient_id: null }));
    } else {
      // Byter till enkelbokning - rensa gruppdata
      setGroupParticipants([]);
      setGroupName('');
      setSelectedTemplateId('');
      setTemplateWaitlist([]);
    }
  };

  // Skydd mot att en långsam medlemshämtning för ett TIDIGARE gruppval
  // skriver över det senaste valets deltagarlista
  const templateReqRef = useRef('');
  const selectedPnrRef = useRef(null);

  // Välj grupp: gruppnamnet sätts från gruppen och ALLA medlemmar följer
  // med som deltagare (Spår P2). Byte av grupp ersätter deltagarlistan.
  const handleTemplateChange = async (templateId) => {
    // Plock från väntelistan är rent klientstate - varna innan det kastas
    if (groupParticipants.some(p => p.from_waitlist) &&
        !window.confirm('Byte av grupp ersätter deltagarlistan - dina tillägg från väntelistan försvinner. Vill du fortsätta?')) {
      return;
    }
    setSelectedTemplateId(templateId);
    setWaitlistChoice(null);
    templateReqRef.current = templateId;
    if (!templateId) {
      setGroupName('');
      setGroupParticipants([]);
      return;
    }
    const template = groupTemplates.find(t => t.template_id === parseInt(templateId));
    setGroupName(template ? template.name : '');
    try {
      const members = await getGroupMembers(templateId);
      if (templateReqRef.current !== templateId) return; // nyare val vann
      setGroupParticipants((members || []).map(m => ({
        patient_id: m.patient_id,
        fullname: m.patient?.fullname,
        personnummer: m.patient?.personnummer,
        status: 'confirmed',
        is_member: true
      })));
    } catch (err) {
      console.error('Kunde inte hämta gruppens medlemmar:', err);
      if (templateReqRef.current !== templateId) return;
      setGroupParticipants([]);
    }
  };

  // Uppdatera varaktighet baserat på bokningstyp
  const handleTypeChange = (typeId) => {
    setFormData(prev => ({ ...prev, appointment_type_id: typeId }));

    if (typeId) {
      const selectedType = appointmentTypes.find(t => t.type_id === parseInt(typeId));
      if (selectedType) {
        setFormData(prev => ({ ...prev, duration_minutes: selectedType.duration_minutes }));
      }
    }
  };

  // Beräkna sluttid från starttid + varaktighet (utan UTC-konvertering)
  const calculateEndTime = () => {
    if (!formData.start_time) return null;

    const startDate = new Date(formData.start_time);
    // Använd duration_minutes eller default 30
    const duration = formData.duration_minutes || 30;
    const endDate = new Date(startDate.getTime() + duration * 60000);
    return formatDateTimeLocal(endDate);
  };

  // Ny patient (gemensamt formulär) — eller befintlig — väljs direkt i bokningen
  const handlePatientFromForm = (patient) => {
    handleSelectPatient(patient);
    setShowPatientForm(false);
  };

  // Ny (eller befintlig) patient läggs direkt till som deltagare i gruppen (G2).
  // Sker utan att lämna modalen - påbörjat gruppformulär behålls.
  const handleParticipantFromForm = (patient) => {
    setGroupParticipants(prev => {
      if (prev.some(p => p.patient_id === patient.patient_id)) return prev;
      return [...prev, {
        patient_id: patient.patient_id,
        fullname: patient.fullname,
        personnummer: patient.personnummer,
        status: 'confirmed'
      }];
    });
    setShowNewParticipantForm(false);
    setError(null);
  };

  // Öppna redigering av patientens kontaktuppgifter
  const handleEditPatientContact = () => {
    setPatientContactEdit({
      email: selectedPatient?.email || '',
      phone: selectedPatient?.phone || ''
    });
    setEditingPatientContact(true);
  };

  // Spara patientens kontaktuppgifter
  const handleSavePatientContact = async () => {
    if (!selectedPatient) return;

    setSavingPatientContact(true);
    try {
      const updatedPatient = await updatePatient(selectedPatient.patient_id, {
        email: patientContactEdit.email,
        phone: patientContactEdit.phone
      });
      // Uppdatera selectedPatient med nya uppgifter
      setSelectedPatient({
        ...selectedPatient,
        email: updatedPatient.email || patientContactEdit.email,
        phone: updatedPatient.phone || patientContactEdit.phone
      });
      setEditingPatientContact(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte spara kontaktuppgifter');
    } finally {
      setSavingPatientContact(false);
    }
  };

  // Spara bokning
  const handleSubmit = async (e) => {
    e.preventDefault();
    doSubmit();
  };

  // extra spiller in i request-datat - används av kollisionsfrågan (I2) för
  // omförsök med conflict_override eller kortare duration_minutes
  const doSubmit = async (extra = {}) => {
    setError(null);

    if (!formData.start_time) {
      setError('Starttid krävs');
      return;
    }

    if (!formData.appointment_type_id) {
      setError('Bokningstyp krävs');
      return;
    }

    // Validering för gruppbokning: vid nybokning väljs en befintlig grupp
    // (grupper skapas i Grupper-vyn, inte här - Spår P2)
    if (isGroupBooking) {
      if (!isEditing && !selectedTemplateId) {
        setError('Välj en grupp - grupper skapas under Grupper i menyn');
        return;
      }
      if (!groupName.trim()) {
        setError('Gruppnamn krävs för gruppbokning');
        return;
      }
      // Gruppserie kräver slutdatum, högst 12 mån fram (Spår G)
      if (!isEditing && recurrencePattern !== 'none') {
        if (!recurrenceEndDate) {
          setError('En återkommande gruppbokning kräver ett slutdatum');
          return;
        }
        if (recurrenceEndDate > maxGroupSeriesEndDate()) {
          setError(`Slutdatumet får vara högst ${GROUP_SERIES_MAX_MONTHS} månader fram`);
          return;
        }
      }
      // Deltagare är valfritt - kan läggas till senare
    } else if (!formData.patient_id) {
      // Enkelbokning kräver en vald patient
      setError('Välj en patient först');
      return;
    }

    // Schema-varning kräver override
    if (scheduleWarning && !scheduleOverride) {
      setError('Bekräfta bokning utanför arbetstid genom att kryssa i rutan');
      return;
    }

    try {
      setLoading(true);

      if (isGroupBooking) {
        // Gruppbokning
        const groupData = {
          group_name: groupName,
          appointment_type_id: formData.appointment_type_id || null,
          start_time: formData.start_time,
          duration_minutes: formData.duration_minutes || 30,
          notes: formData.notes,
          user_id: formData.user_id ? parseInt(formData.user_id) : null,
          // Vid redigering skickas listan bara om den ändrats (synkas då med valt omfång)
          ...((!isEditing || participantsChanged)
            ? { participant_ids: groupParticipants.map(p => p.patient_id),
                participant_scope: participantScope }
            : {}),
          schedule_override: scheduleOverride,
          recurrence_pattern: recurrencePattern,
          recurrence_end_date: recurrenceEndDate || null,
          ...confirmationFields(),
          ...extra
        };

        if (isEditing) {
          // Uppdatera befintlig gruppbokning
          await saveEdit({ ...groupData, is_group: true });
        } else {
          await createGroupAppointment(groupData);

          // Permanent-val från väntelistan verkställs först NÄR bokningen
          // faktiskt skapats (Spår P2) - Avbryt/misslyckad bokning lämnar
          // därmed inga halvgjorda gruppändringar. Engångsdeltagare ligger
          // avsiktligt kvar i väntelistan.
          const pendingMoves = groupParticipants.filter(
            p => p.pending_member && p.waitlist_id
          );
          if (pendingMoves.length > 0) {
            const failedNames = [];
            await Promise.all(pendingMoves.map(p =>
              moveWaitlistToMember(p.waitlist_template_id || selectedTemplateId, p.waitlist_id)
                .catch(err => {
                  console.error('Kunde inte flytta in i gruppen:', err);
                  failedNames.push(p.fullname || `patient ${p.patient_id}`);
                })
            ));
            if (failedNames.length > 0) {
              window.alert(
                `Bokningen skapades, men följande kunde inte flyttas in i gruppen ` +
                `(t.ex. full grupp): ${failedNames.join(', ')}. De ligger kvar i väntelistan.`
              );
            }
          }
        }
      } else {
        // Enkelbokning
        const data = {
          patient_id: formData.patient_id,
          appointment_type_id: formData.appointment_type_id || null,
          start_time: formData.start_time,
          duration_minutes: formData.duration_minutes || 30,
          notes: formData.notes,
          status: formData.status,
          user_id: formData.user_id ? parseInt(formData.user_id) : null,
          schedule_override: scheduleOverride,
          recurrence_pattern: recurrencePattern,
          recurrence_end_date: recurrenceEndDate || null,
          ...extra
        };

        if (isEditing) {
          await saveEdit(data);
        } else {
          const createdAppointment = await createAppointment({ ...data, ...confirmationFields() });

          // Om användaren valt en kallelsemall, öppna KallelseModal med mallen förvald
          if (selectedKallelseTemplateId && onOpenKallelseModal && createdAppointment) {
            // Bygg upp appointment-objekt med patientdata för KallelseModal
            const appointmentWithPatient = {
              ...createdAppointment,
              patient: selectedPatient
            };
            // Hitta vald mall
            const selectedTemplate = kallelseTemplates.find(
              t => t.template_id === parseInt(selectedKallelseTemplateId)
            );
            onSave();
            onOpenKallelseModal(appointmentWithPatient, selectedTemplate);
            return; // Avsluta tidigt för att inte anropa onSave() igen
          }
        }
      }

      setConflictPrompt(null);
      onSave();
    } catch (err) {
      console.error('Error saving appointment:', err);
      const errorMsg = err.response?.data?.error || 'Kunde inte spara bokningen';
      // Kollisionsfråga (I2): längden ryms inte före nästa bokning/block -
      // fråga dubbelboka / ta kortare tid / avbryt i stället för att spara tyst
      if (err.response?.data?.requires_conflict_override) {
        setConflictPrompt({
          message: errorMsg,
          availableMinutes: err.response.data.available_minutes || 0
        });
      } else if (err.response?.data?.requires_override) {
        // Hantera schedule override-kravet från backend
        setScheduleWarning(errorMsg);
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Spara en redigering mot rätt bokning (Spår R). Vanliga bokningar, modern
  // och redan materialiserade tillfällen uppdateras som förut.
  const saveEdit = async (data) => {
    if (!isVirtualInstance) {
      await updateAppointment(appointment.appointment_id, data);
      return;
    }
    // Upprepningen redigeras inte härifrån
    const { recurrence_pattern, recurrence_end_date, ...rest } = data;
    if (editScope === 'series') {
      // Hela serien: behåll seriens startdatum, ta tid-på-dygnet från
      // formuläret. Status och deltagare är per tillfälle och följer inte med.
      const { status, participant_ids, participant_scope, ...seriesData } = rest;
      const seriesDate = String(appointment.series_start_time || appointment.start_time).slice(0, 10);
      const timeOfDay = String(rest.start_time).slice(11);
      await updateAppointment(appointment.appointment_id, {
        ...seriesData,
        start_time: `${seriesDate}T${timeOfDay}`
      });
      return;
    }
    const real = await ensureRealAppointment(appointment);
    await updateAppointment(real.appointment_id, rest);
  };

  // Kollisionsfrågans val (I2)
  const handleConflictDoubleBook = () => {
    setConflictPrompt(null);
    doSubmit({ conflict_override: true });
  };

  const handleConflictTakeShorter = () => {
    const mins = conflictPrompt?.availableMinutes;
    if (!mins) return;
    setConflictPrompt(null);
    setFormData(prev => ({ ...prev, duration_minutes: mins }));
    // Servern kollar om igen med den kortare längden
    doSubmit({ duration_minutes: mins });
  };

  // Enkelbokning kräver en vald patient innan den kan sparas.
  // Gruppbokning har deltagare i stället och styrs av gruppnamn (kan sakna deltagare).
  const patientMissing = !isGroupBooking && !formData.patient_id;

  // Behandlaren bokningen gäller - visas i headern så man ser vem man bokar
  // hos (viktigt t.ex. från "Hitta tid" med Alla behandlare)
  const bookingUser = (clinicUsers || []).find(
    u => String(u.user_id) === String(formData.user_id)
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content appointment-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {isEditing ? 'Redigera bokning' : 'Ny bokning'}
            {bookingUser && (
              <span className="modal-header-user">
                <i className="fas fa-user-md"></i>
                hos {bookingUser.first_name} {bookingUser.last_name}
              </span>
            )}
          </h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="calendar-error">{error}</div>}

            {/* Spår K2.2: vem som bokade (saknas för bokningar från före K2.2) */}
            {bookedByLine && (
              <div className="booked-by-line">
                <i className="fas fa-user-edit"></i> {bookedByLine}
              </div>
            )}

            {/* Spår R: tillfälle i återkommande serie - vad gäller ändringen? */}
            {isVirtualInstance && (
              <div className="form-group recurrence-section" style={{ marginBottom: '15px' }}>
                <label><i className="fas fa-redo"></i> Återkommande bokning – ändringen gäller</label>
                <div className="recurrence-options" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                  <label className="recurrence-option">
                    <input
                      type="radio"
                      name="edit-scope"
                      value="occurrence"
                      checked={editScope === 'occurrence'}
                      onChange={() => setEditScope('occurrence')}
                    />
                    <span>Bara detta tillfälle</span>
                  </label>
                  <label className="recurrence-option">
                    <input
                      type="radio"
                      name="edit-scope"
                      value="series"
                      checked={editScope === 'series'}
                      onChange={() => setEditScope('series')}
                    />
                    <span>Hela serien</span>
                  </label>
                </div>
                {editScope === 'series' && (
                  <small className="end-time-preview">
                    Tid, längd, typ, behandlare och anteckning ändras för alla tillfällen som inte
                    redan har egna uppgifter. Datum och status ändras inte för serien.
                  </small>
                )}
              </div>
            )}
            {/* Moderbokningen = seriens första tillfälle OCH seriens mall */}
            {isEditing && !isVirtualInstance && !appointment.parent_id
              && appointment.recurrence_pattern && appointment.recurrence_pattern !== 'none' && (
              <div className="warning-message" style={{ marginBottom: '15px' }}>
                <i className="fas fa-redo"></i> Första tillfället i en återkommande serie: ändrad
                tid, dag, längd, typ eller behandlare gäller hela serien (tillfällen som redan har
                egna uppgifter påverkas inte). Status gäller bara detta tillfälle; för ändrad
                deltagarlista väljer du omfång nedan.
              </div>
            )}

            {/* Kollisionsfråga (I2): längden ryms inte - fråga i stället för
                att tyst spara överlappande/trunkerat */}
            {conflictPrompt && (
              <div className="conflict-prompt">
                <div className="warning-message">
                  <i className="fas fa-exclamation-triangle"></i> {conflictPrompt.message}
                </div>
                <div className="conflict-prompt-actions">
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={handleConflictDoubleBook}
                    disabled={loading}
                  >
                    Dubbelboka ändå
                  </button>
                  {conflictPrompt.availableMinutes >= 5 && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleConflictTakeShorter}
                      disabled={loading}
                    >
                      Ta {conflictPrompt.availableMinutes} min
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setConflictPrompt(null)}
                    disabled={loading}
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            )}

            {/* Schema-varning */}
            {scheduleWarning && (
              <div className="schedule-warning">
                <div className="warning-message">
                  <i className="fas fa-exclamation-triangle"></i> {scheduleWarning}
                </div>
                <label className="override-checkbox">
                  <input
                    type="checkbox"
                    checked={scheduleOverride}
                    onChange={e => setScheduleOverride(e.target.checked)}
                  />
                  Boka ändå utanför arbetstid
                </label>
              </div>
            )}

            {/* Gruppbokning toggle */}
            {!isEditing && (
              <div className="form-group booking-type-toggle">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={isGroupBooking}
                    onChange={handleToggleGroupBooking}
                  />
                  <span className="toggle-text">Gruppbokning</span>
                </label>
              </div>
            )}

            {/* Gruppval (nybokning: välj befintlig grupp - skapas i Grupper-vyn) */}
            {isGroupBooking && !isEditing && (
              <div className="form-group">
                <label>Grupp *</label>
                <select
                  value={selectedTemplateId}
                  onChange={e => handleTemplateChange(e.target.value)}
                  className="template-dropdown"
                >
                  <option value="">-- Välj grupp --</option>
                  {groupTemplates.map(template => (
                    <option key={template.template_id} value={template.template_id}>
                      {template.name} ({template.member_count || 0} medlemmar, {template.waitlist_count || 0} i väntelistan)
                    </option>
                  ))}
                </select>
                {groupTemplates.length === 0 && (
                  <p className="no-participants-hint" style={{ marginTop: '6px' }}>
                    <i className="fas fa-info-circle"></i>{' '}
                    Inga grupper finns än - skapa dem under Grupper i menyn till vänster.
                  </p>
                )}
              </div>
            )}

            {/* Gruppnamn (redigering av befintlig gruppbokning) */}
            {isGroupBooking && isEditing && (
              <div className="form-group">
                <label>Gruppnamn *</label>
                <input
                  type="text"
                  value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                  placeholder="T.ex. Mindfulness grupp A, KBT-grupp..."
                  required
                />
              </div>
            )}

            {/* Patient (enkelbokning) / Deltagare (gruppbokning) */}
            {!isGroupBooking ? (
              // Enkelbokning - välj en patient
              <div className="form-group">
                <label>Patient</label>
                {!selectedPatient && !showPatientForm && (
                  <div className="patient-search">
                    <input
                      type="text"
                      className="patient-search-input"
                      placeholder="Sök patient (namn eller personnummer)..."
                      value={patientSearch}
                      onChange={e => setPatientSearch(e.target.value)}
                      onFocus={() => setPatientFocused(true)}
                      onBlur={() => setPatientFocused(false)}
                    />
                    {patientResults.length > 0 && (
                      <div className="patient-search-results">
                        {patientSearch.length < 2 && (
                          <div className="patient-search-heading">
                            <i className="fas fa-history"></i> Senaste patienter
                          </div>
                        )}
                        {patientResults.map(patient => (
                          <div
                            key={patient.patient_id}
                            className="patient-search-item"
                            onClick={() => handleSelectPatient(patient)}
                          >
                            <div className="patient-name">{patient.fullname}</div>
                            <div className="patient-pnr">{formatPnrPasis(patient.personnummer, patient.id_type)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ marginTop: '8px' }}
                      onClick={() => setShowPatientForm(true)}
                    >
                      + Ny patient
                    </button>
                    <div className="patient-required-hint">
                      <i className="fas fa-info-circle"></i>
                      Välj en patient först för att kunna boka
                    </div>
                  </div>
                )}

                {selectedPatient && (
                  <div className="selected-patient-container">
                    <div className="selected-patient">
                      <div className="patient-info">
                        {selectedPatient.fullname}{selectedPatient.personnummer && (<>
                          {' ('}<span ref={selectedPnrRef}>{formatPnrPasis(selectedPatient.personnummer, selectedPatient.id_type)}</span>{')'}
                          <CopyPnrButton
                            personnummer={selectedPatient.personnummer}
                            idType={selectedPatient.id_type}
                            textRef={selectedPnrRef}
                          />
                        </>)}
                        {noShowCount > 0 && (
                          <span
                            className="appointment-no-show-badge"
                            title="Uteblivna besök senaste 12 månaderna"
                            style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 10, background: '#fee2e2', color: '#b91c1c', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}
                          >
                            <i className="fas fa-user-slash"></i> {noShowCountLabel(noShowCount)}
                          </span>
                        )}
                      </div>
                      <button type="button" className="clear-btn" onClick={handleClearPatient}>
                        &times;
                      </button>
                    </div>

                    {/* Kontaktuppgifter */}
                    {!editingPatientContact ? (
                      <div className="patient-contact-display">
                        <div className="contact-row">
                          <span className="contact-label">
                            <i className="fas fa-envelope"></i> E-post:
                          </span>
                          <span className={selectedPatient.email ? 'contact-value' : 'contact-missing'}>
                            {selectedPatient.email || 'Ej angiven'}
                          </span>
                        </div>
                        <div className="contact-row">
                          <span className="contact-label">
                            <i className="fas fa-phone"></i> Telefon:
                          </span>
                          <span className={selectedPatient.phone ? 'contact-value' : 'contact-missing'}>
                            {selectedPatient.phone || 'Ej angiven'}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="edit-contact-btn"
                          onClick={handleEditPatientContact}
                        >
                          <i className="fas fa-pencil-alt"></i> Redigera
                        </button>
                      </div>
                    ) : (
                      <div className="patient-contact-edit">
                        <div className="contact-edit-row">
                          <label>
                            <i className="fas fa-envelope"></i> E-post
                          </label>
                          <input
                            type="email"
                            value={patientContactEdit.email}
                            onChange={e => setPatientContactEdit(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="patient@email.se"
                          />
                        </div>
                        <div className="contact-edit-row">
                          <label>
                            <i className="fas fa-phone"></i> Telefon
                          </label>
                          <input
                            type="tel"
                            value={patientContactEdit.phone}
                            onChange={e => setPatientContactEdit(prev => ({ ...prev, phone: e.target.value }))}
                            placeholder="070-123 45 67"
                          />
                        </div>
                        <div className="contact-edit-buttons">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={handleSavePatientContact}
                            disabled={savingPatientContact}
                          >
                            {savingPatientContact ? 'Sparar...' : 'Spara'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => setEditingPatientContact(false)}
                            disabled={savingPatientContact}
                          >
                            Avbryt
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {showPatientForm && (
                <div style={{ marginTop: '10px', padding: '15px', background: '#f8f9fa', borderRadius: '4px' }}>
                  <h4 style={{ marginTop: 0 }}>Ny patient</h4>
                  <NewPatientForm
                    compact
                    showAddress={false}
                    onCreated={handlePatientFromForm}
                    onExisting={handlePatientFromForm}
                    existingLabel="Välj patienten"
                    onCancel={() => setShowPatientForm(false)}
                  />
                </div>
              )}
              </div>
            ) : (
              // Gruppbokning - deltagare kommer från gruppen + väntelistan
              <div className="form-group">
                {/* Väntelista för vald grupp: plocka in med permanent/engångs-val */}
                {!isEditing && selectedTemplateId && (
                  <div className="waitlist-from-template">
                    {loadingWaitlist ? (
                      <div className="waitlist-loading">Laddar väntelista...</div>
                    ) : templateWaitlist.length > 0 ? (
                      <>
                        <div className="waitlist-header-row">
                          <span className="waitlist-title">Väntelista ({templateWaitlist.length})</span>
                          {waitlistChoice === 'all' ? (
                            <span className="waitlist-choice">
                              Lägg till alla:
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => applyWaitlistAdd(templateWaitlist, true)}
                              >
                                Permanent
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => applyWaitlistAdd(templateWaitlist, false)}
                              >
                                Bara denna gång
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => setWaitlistChoice(null)}
                              >
                                Avbryt
                              </button>
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => setWaitlistChoice('all')}
                            >
                              <i className="fas fa-plus"></i> Lägg till alla
                            </button>
                          )}
                        </div>
                        <div className="waitlist-items-compact">
                          {templateWaitlist.map(entry => {
                            const isAdded = groupParticipants.some(p => p.patient_id === entry.patient?.patient_id);
                            const isAsking = waitlistChoice === entry.waitlist_id;
                            return (
                              <div
                                key={entry.waitlist_id}
                                className={`waitlist-item-compact ${isAdded ? 'added' : ''} ${isAsking ? 'asking' : ''}`}
                                onClick={() => !isAdded && !isAsking && setWaitlistChoice(entry.waitlist_id)}
                              >
                                <span className="waitlist-name">{entry.patient?.fullname}</span>
                                {isAdded ? (
                                  <span className="added-badge"><i className="fas fa-check"></i></span>
                                ) : isAsking ? (
                                  <span className="waitlist-choice" onClick={e => e.stopPropagation()}>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      title="Flyttas in i gruppen och tas bort från väntelistan"
                                      onClick={() => applyWaitlistAdd([entry], true)}
                                    >
                                      Permanent
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary"
                                      title="Deltar bara på denna bokning och ligger kvar i väntelistan"
                                      onClick={() => applyWaitlistAdd([entry], false)}
                                    >
                                      Bara denna gång
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary"
                                      onClick={() => setWaitlistChoice(null)}
                                    >
                                      <i className="fas fa-times"></i>
                                    </button>
                                  </span>
                                ) : (
                                  <span className="add-btn"><i className="fas fa-plus"></i></span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="waitlist-empty">Ingen i väntelistan för denna grupp</div>
                    )}
                  </div>
                )}

                <label style={{ marginTop: selectedTemplateId ? '16px' : '0' }}>
                  Deltagare ({groupParticipants.length})
                </label>

                {/* "Hela serien" ändrar inte deltagare - de är per tillfälle (Spår G) */}
                {rosterLocked && (
                  <p className="no-participants-hint" style={{ marginTop: '4px' }}>
                    <i className="fas fa-info-circle"></i>{' '}
                    Deltagare ändras per tillfälle — välj "Bara detta tillfälle" ovan eller
                    använd Hantera deltagare.
                  </p>
                )}

                {/* Fritt deltagarsök bara vid redigering - vid nybokning kommer
                    deltagarna från gruppen och väntelistan (Spår P2) */}
                {isEditing && !rosterLocked && (
                <div className="participant-search">
                  <input
                    type="text"
                    className="patient-search-input"
                    placeholder="Sök patient att lägga till..."
                    value={participantSearch}
                    onChange={e => setParticipantSearch(e.target.value)}
                  />
                  {participantResults.length > 0 && (
                    <div className="patient-search-results">
                      {participantResults.map(patient => (
                        <div
                          key={patient.patient_id}
                          className="patient-search-item"
                          onClick={() => handleAddParticipant(patient)}
                        >
                          <div className="patient-name">{patient.fullname}</div>
                          <div className="patient-pnr">{patient.personnummer}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                {/* Lägg till HELT ny patient som deltagare - utan att lämna modalen (G2) */}
                {!isEditing || rosterLocked ? null : !showNewParticipantForm ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ marginTop: '8px' }}
                    onClick={() => setShowNewParticipantForm(true)}
                  >
                    + Ny patient
                  </button>
                ) : (
                  <div style={{ marginTop: '10px', padding: '15px', background: '#f8f9fa', borderRadius: '4px' }}>
                    <h4 style={{ marginTop: 0 }}>Ny patient som deltagare</h4>
                    <NewPatientForm
                      compact
                      showAddress={false}
                      submitLabel="Skapa och lägg till"
                      onCreated={handleParticipantFromForm}
                      onExisting={handleParticipantFromForm}
                      existingLabel="Lägg till som deltagare"
                      onCancel={() => setShowNewParticipantForm(false)}
                    />
                  </div>
                )}

                {/* Lista över tillagda deltagare */}
                {groupParticipants.length > 0 && (
                  <div className="participant-list">
                    {groupParticipants.map(participant => (
                      <div key={participant.patient_id} className="participant-item">
                        <div className="participant-info">
                          <span className="participant-name">{participant.fullname || 'Okänd patient'}</span>
                          <span className="participant-pnr">{participant.personnummer || ''}</span>
                          {participant.is_member && (
                            <span className="from-waitlist-badge">medlem</span>
                          )}
                          {participant.pending_member && (
                            <span className="from-waitlist-badge">blir medlem vid bokning</span>
                          )}
                          {participant.from_waitlist && !participant.pending_member && (
                            <span className="from-waitlist-badge">från väntelistan (denna gång)</span>
                          )}
                        </div>
                        {!rosterLocked && (
                          <button
                            type="button"
                            className="remove-participant-btn"
                            onClick={() => handleRemoveParticipant(participant.patient_id)}
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {groupParticipants.length === 0 && !selectedTemplateId && !isEditing && (
                  <div className="no-participants-hint">
                    <i className="fas fa-info-circle"></i>
                    Välj en grupp ovan så följer gruppens medlemmar med automatiskt
                  </div>
                )}

                {/* Ändrad deltagarlista i en serie: bara detta tillfälle eller även kommande */}
                {isEditing && !rosterLocked && participantsChanged && isSeriesAppointment(appointment) && (
                  <div className="recurrence-section" style={{ marginTop: '10px' }}>
                    <label><i className="fas fa-redo"></i> Ändrad deltagarlista gäller</label>
                    <div className="recurrence-options" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                      <label className="recurrence-option">
                        <input
                          type="radio"
                          name="participant-scope"
                          value="this"
                          checked={participantScope === 'this'}
                          onChange={() => setParticipantScope('this')}
                        />
                        <span>Bara detta tillfälle</span>
                      </label>
                      <label className="recurrence-option">
                        <input
                          type="radio"
                          name="participant-scope"
                          value="following"
                          checked={participantScope === 'following'}
                          onChange={() => setParticipantScope('following')}
                        />
                        <span>Detta och alla kommande</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Visa deltagare-modal för befintlig gruppbokning */}
                {isEditing && appointment?.is_group && onOpenGroupParticipants && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ marginTop: '8px' }}
                    onClick={() => {
                      onClose();
                      onOpenGroupParticipants(appointment);
                    }}
                  >
                    Hantera deltagare
                  </button>
                )}
              </div>
            )}

            {/* Bokningstyp, Starttid, Varaktighet - i rad */}
            <div className="form-row-3" style={{ marginBottom: '15px' }}>
              <div className="form-group">
                <label>Bokningstyp *</label>
                <select
                  value={formData.appointment_type_id}
                  onChange={e => handleTypeChange(e.target.value)}
                  required
                >
                  <option value="" disabled>-- Välj typ --</option>
                  {appointmentTypes.map(type => (
                    <option key={type.type_id} value={type.type_id}>
                      {type.name} ({type.duration_minutes} min)
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Starttid *</label>
                <input
                  type="datetime-local"
                  value={formData.start_time}
                  onChange={e => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                  required
                />
              </div>

              <div className="form-group">
                <label>Varaktighet</label>
                <div className="duration-input-group">
                  <input
                    type="number"
                    min="5"
                    max="480"
                    step="5"
                    value={formData.duration_minutes}
                    onChange={e => setFormData(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 30 }))}
                  />
                  <div className="duration-presets">
                    {[15, 30, 45, 60, 90].map(mins => (
                      <button
                        key={mins}
                        type="button"
                        className={`duration-preset ${formData.duration_minutes === mins ? 'active' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, duration_minutes: mins }))}
                      >
                        {mins}
                      </button>
                    ))}
                  </div>
                </div>
                {formData.start_time && formData.duration_minutes && (
                  <small className="end-time-preview">
                    Sluttid: {new Date(new Date(formData.start_time).getTime() + formData.duration_minutes * 60000)
                      .toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                  </small>
                )}
              </div>
            </div>

            {/* Status och Anteckningar - i rad */}
            <div className="form-row" style={{ marginBottom: '15px' }}>
              {isEditing && (
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="scheduled">Bokad</option>
                    <option value="completed">Genomförd</option>
                    <option value="cancelled">Avbokad</option>
                    <option value="no_show">Uteblev</option>
                  </select>
                  {isGroupBooking && appointment?.status === 'cancelled' && formData.status !== 'cancelled' && (
                    <small className="end-time-preview">
                      Deltagarna förblir avbokade — lägg till dem igen under Hantera deltagare.
                    </small>
                  )}
                </div>
              )}

              <div className="form-group" style={!isEditing ? { gridColumn: '1 / -1' } : {}}>
                <label>Anteckningar</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Anteckningar om bokningen..."
                  style={{ minHeight: '60px' }}
                />
              </div>
            </div>

            {/* Bokningsbekräftelse (Spår B2/B3) - ny bokning, eller nya deltagare i en grupp */}
            {showConfirmationChoice && (
              <BookingConfirmationChoice
                options={confirmationOptions}
                value={confirmation}
                onChange={setConfirmation}
                patient={isGroupBooking ? null : selectedPatient}
                startTime={isEditing ? null : formData.start_time}
                title={isGroupBooking
                  ? (isEditing ? 'Skicka bokningsbekräftelse till nya deltagare' : 'Skicka bokningsbekräftelse till deltagarna')
                  : 'Skicka bokningsbekräftelse'}
              />
            )}

            {/* Skickat (utskickslogg) - bara vid redigering av bokning med patient.
                Virtuella tillfällen (Spår R) hanteras i sektionen: aldrig seriens utskick. */}
            {isEditing && appointment.patient_id && (
              <AppointmentCommunicationsSection appointment={appointment} />
            )}

            {/* Upprepning och Kallelse - sida vid sida */}
            {!isEditing && (
              <div className="form-row" style={{ marginBottom: '15px' }}>
                {/* Upprepning */}
                <div className="form-group recurrence-section" style={{ margin: 0 }}>
                  <label><i className="fas fa-redo"></i> Upprepa bokning</label>
                  <div className="recurrence-options" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                    <label className="recurrence-option">
                      <input
                        type="radio"
                        name="recurrence"
                        value="none"
                        checked={recurrencePattern === 'none'}
                        onChange={() => setRecurrencePattern('none')}
                      />
                      <span>Ingen</span>
                    </label>
                    <label className="recurrence-option">
                      <input
                        type="radio"
                        name="recurrence"
                        value="daily"
                        checked={recurrencePattern === 'daily'}
                        onChange={() => setRecurrencePattern('daily')}
                      />
                      <span>Dagligen</span>
                    </label>
                    <label className="recurrence-option">
                      <input
                        type="radio"
                        name="recurrence"
                        value="weekly"
                        checked={recurrencePattern === 'weekly'}
                        onChange={() => setRecurrencePattern('weekly')}
                      />
                      <span>Veckovis</span>
                    </label>
                    <label className="recurrence-option">
                      <input
                        type="radio"
                        name="recurrence"
                        value="biweekly"
                        checked={recurrencePattern === 'biweekly'}
                        onChange={() => setRecurrencePattern('biweekly')}
                      />
                      <span>Varannan v.</span>
                    </label>
                  </div>

                  {recurrencePattern !== 'none' && (
                    <div className="recurrence-end-date" style={{ marginTop: '10px', paddingTop: '10px' }}>
                      <label>Slutdatum{isGroupBooking ? ' *' : ''}</label>
                      <input
                        type="date"
                        value={recurrenceEndDate}
                        onChange={e => setRecurrenceEndDate(e.target.value)}
                        min={formData.start_time ? formData.start_time.split('T')[0] : ''}
                        max={isGroupBooking ? maxGroupSeriesEndDate() : undefined}
                        required={isGroupBooking}
                      />
                      {isGroupBooking && (
                        <small className="end-time-preview">
                          Krävs för gruppserier, högst {GROUP_SERIES_MAX_MONTHS} månader fram.
                          Alla tillfällen skapas direkt med deltagarna.
                        </small>
                      )}
                    </div>
                  )}
                </div>

                {/* Kallelse - endast vid ny bokning med patient */}
                {!isGroupBooking && selectedPatient && onOpenKallelseModal && kallelseTemplates.length > 0 && (
                  <div className="form-group send-kallelse-option" style={{ margin: 0 }}>
                    <label>
                      <i className="fas fa-envelope"></i> Skicka kallelse efter bokning
                    </label>
                    <select
                      value={selectedKallelseTemplateId}
                      onChange={e => setSelectedKallelseTemplateId(e.target.value)}
                      className="kallelse-template-select"
                    >
                      <option value="">-- Ingen kallelse --</option>
                      {kallelseTemplates.map(template => (
                        <option key={template.template_id} value={template.template_id}>
                          {template.template_name}
                        </option>
                      ))}
                    </select>
                    {selectedKallelseTemplateId && (
                      <div className="kallelse-email-info">
                        {selectedPatient.email ? (
                          <span className="email-preview">
                            <i className="fas fa-check-circle"></i> Skickas till: {selectedPatient.email}
                          </span>
                        ) : (
                          <span className="no-email-warning-small">
                            <i className="fas fa-exclamation-triangle"></i> Patienten saknar e-post
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <div className="left-buttons">
              {isEditing && (
                <>
                  {/* Bokningar tas aldrig bort (Spår K1): felbokning avbokas,
                      en återkommande serie avslutas från ett datum */}
                  {onEndSeries && (isVirtualInstance || appointment.parent_id
                    || (appointment.recurrence_pattern && appointment.recurrence_pattern !== 'none')) && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => onEndSeries(appointment)}
                      title="Avbokar tillfällena från och med valt datum - tidigare tillfällen ligger kvar"
                    >
                      Avsluta serien…
                    </button>
                  )}
                  {appointment.status !== 'cancelled' && (
                    <button
                      type="button"
                      className="btn btn-warning"
                      onClick={() => onCancel(appointment)}
                    >
                      Avboka
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="right-buttons">
              {/* Betalningsstatus/knapp */}
              {isEditing && appointment.status !== 'cancelled' && (
                <>
                  {appointment.is_paid ? (
                    <span className="paid-badge">
                      <i className="fas fa-check-circle"></i> Betald ({appointment.total_paid} kr)
                    </span>
                  ) : (
                    onOpenPaymentModal && (
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => {
                          onClose();
                          onOpenPaymentModal(appointment);
                        }}
                      >
                        <i className="fas fa-cash-register"></i> Registrera betalning
                      </button>
                    )
                  )}
                </>
              )}
              {/* Kallelse-knapp */}
              {isEditing && appointment.status !== 'cancelled' && !isGroupBooking && onOpenKallelseModal && (
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={() => {
                    onClose();
                    onOpenKallelseModal(appointment);
                  }}
                  title={selectedPatient?.email ? 'Skicka kallelse via e-post' : 'Patienten saknar e-postadress'}
                >
                  <i className="fas fa-envelope"></i> Skicka kallelse
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Stäng
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || patientMissing}
                title={patientMissing ? 'Välj en patient först' : undefined}
              >
                {loading ? 'Sparar...' : (isEditing ? 'Uppdatera' : 'Boka')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentModal;
